// @ts-check
// src/lib/network-state.js
// 2026-05-29 EXTRACTED from src/App.jsx (C1 refactor — network/session module-level
// mutable state) as a behavior-preserving TRANSFORM.
//
// Why extract:
//   - These seven bindings are the live network/session/sync state shared across
//     apiGet / apiSave / flushPendingSaves / connectWebSocket / handleConflictResolution
//     and the page-level components / handlers that drive them (login grace, 401
//     logout race, echo-save throttle, conflict merge, data-loss guard, WS fan-out).
//     Keeping them inside the 6741-LOC App.jsx monolith forces every reader/writer to
//     live in-file. Hoisting them into a tiny module-singleton lets the network layer
//     + leaf components import the SAME state без App.jsx coupling (build-time graph,
//     test-time mocking, circular-dependency risk) — same precedent as
//     src/lib/connection-state.js, src/lib/data-versions.js and src/lib/tombstones.js.
//
// Read/write contract (ESM live bindings):
//   - READS: import the exported binding directly. ESM live bindings reflect
//     in-module updates, so a reader sees the current value AFTER a setter runs.
//     (No accessor function needed for reads — `import { sessionExpiredFlag }` is live.)
//   - WRITES (reassigned bindings): you CANNOT assign to an imported binding from
//     another module ("Cannot assign to 'sessionExpiredFlag' because it is an
//     import"). Every reassignment MUST go through the matching setter exported here.
//     Reassigned: sessionExpiredFlag, justLoggedIn, lastSaveTimestamps, mergeByIDGlobal.
//   - WRITES (in-place mutated containers): conflictSetters / lastKnownCounts are
//     `const` PLAIN OBJECTS and wsListeners is a `const` Set — NEVER reassigned, only
//     mutated in place (obj[key] = …; set.add/.delete). The imported reference points
//     at the SAME instance across modules, so NO setter is needed (and adding one would
//     be a footgun — they MUST stay single shared instances, like the old in-file
//     `const conflictSetters = {}`). A duplicate object would silently split state
//     (e.g. conflict setters registered on one copy, looked up on another → no merge).
//
// Threading model: single-threaded JS — no race on these mutations.

// ── sessionExpiredFlag ───────────────────────────────────────────────────────
// Flag to force logout on 401 (avoids reload loop). Set true when an apiGet/apiSave
// 401 indicates the session is gone; reset false after a successful fetch / probe.
export let sessionExpiredFlag = false;
/**
 * @param {boolean} v
 * @returns {void}
 */
export function setSessionExpiredFlag(v) {
  sessionExpiredFlag = v;
}

// ── justLoggedIn ─────────────────────────────────────────────────────────────
// Grace period — don't expire session right after login. Set true on successful
// login, cleared false ~10s later via setTimeout; suppresses the 401 logout race
// during the initial parallel apiGet storm.
export let justLoggedIn = false;
/**
 * @param {boolean} v
 * @returns {void}
 */
export function setJustLoggedIn(v) {
  justLoggedIn = v;
}

// ── lastSaveTimestamps ───────────────────────────────────────────────────────
// FIX C1: Track when each table was last saved to prevent echo saves.
// { endpoint: stampNow() | legacy epochMs }. Reassigned to {} only by _testResetApiSaveState();
// otherwise mutated in place (lastSaveTimestamps[endpoint] = stampNow()).
/** @type {Record<string, number | { wall: number, mono: number | null }>} */
export let lastSaveTimestamps = {};
/**
 * @param {Record<string, number | { wall: number, mono: number | null }>} v
 * @returns {void}
 */
export function setLastSaveTimestamps(v) {
  lastSaveTimestamps = v;
}

// ── mergeByIDGlobal ──────────────────────────────────────────────────────────
// Global reference to mergeByID (set by React component after mount). Reassigned
// once by AppInner via setMergeByIDGlobal; read by handleConflictResolution to merge
// server data into React state on a 409 conflict.
/** @type {((lsData: any, serverData: any, endpoint: string, source: string) => any) | null} */
export let mergeByIDGlobal = null;
/**
 * @param {((lsData: any, serverData: any, endpoint: string, source: string) => any) | null} v
 * @returns {void}
 */
export function setMergeByIDGlobal(v) {
  mergeByIDGlobal = v;
}

// ── conflictSetters ──────────────────────────────────────────────────────────
// Registered by React component: { 'payments': setPayments, ... }.
// SHARED singleton — mutated in place (conflictSetters[table] = setter), NEVER
// reassigned. A duplicate object would split registration from lookup.
/** @type {Record<string, (data: any) => void>} */
export const conflictSetters = {};

// ── lastKnownCounts ──────────────────────────────────────────────────────────
// Track last known record counts per table to detect data loss.
// SHARED singleton — mutated in place (lastKnownCounts[endpoint] = N), NEVER reassigned.
/** @type {Record<string, number>} */
export const lastKnownCounts = {};

// ── wsListeners ──────────────────────────────────────────────────────────────
// Set of WS update callbacks (e.g. DealsPage offer-labels Object-payload sync).
// SHARED singleton — mutated in place (.add/.delete), NEVER reassigned.
/** @type {Set<(msg: any) => void>} */
export const wsListeners = new Set();

// ── wsPresenceListeners ──────────────────────────────────────────────────────
// Set of presence-count callbacks — notified when server sends presence-count.
/** @type {Set<(p: {count: number, users: string[]}) => void>} */
export const wsPresenceListeners = new Set();
